import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Handshake } from "lucide-react";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { getStripePromise, useStripeConfig } from "../lib/stripe";
import { useAI } from "../hooks/useAI";
import { useJudgeTiming } from "../hooks/useJudgeTiming";
import { useFeeConfig } from "../hooks/useFeeConfig";
import { clearDraft } from "../lib/draftCache";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";
import { COLORS, SANS, formStyles, pageStyles } from "../components/dispute/theme";
import StripeTestCardHint from "../components/dispute/StripeTestCardHint";

function formatMoney(cents) {
  if (cents == null) return "—";
  return `$${(cents / 100).toFixed(2)}`;
}

// The card form (inside <Elements>). Confirms the card, then finalizes server-side.
function PayForm({ uuid, amount, onPaid }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);

  const handlePay = async () => {
    // Guard against clicks before the PaymentElement has mounted — confirmPayment
    // throws an IntegrationError if there's no mounted element yet.
    if (!stripe || !elements || !ready) return;
    setSubmitting(true);
    setError(null);
    const { error: confirmErr } = await stripe.confirmPayment({
      elements,
      redirect: "if_required",
      confirmParams: { return_url: `${window.location.origin}/dispute/${uuid}` },
    });
    if (confirmErr) {
      setError(confirmErr.message || "Payment failed.");
      setSubmitting(false);
      return;
    }
    const result = await onPaid();
    if (result?.error) {
      setError(result.error);
      setSubmitting(false);
    }
  };

  return (
    <div>
      <PaymentElement options={{ layout: "tabs" }} onReady={() => setReady(true)} />
      {error && <div style={styles.payError}>{error}</div>}
      <button
        type="button"
        onClick={handlePay}
        disabled={!stripe || !ready || submitting}
        style={{
          ...formStyles.primaryButton,
          // Blue primary to match SubmitDispute's "File the Dispute" button.
          ...(submitting ? formStyles.primaryButtonDisabled : { background: "#1A5EC4", color: "#fff" }),
          marginTop: 18,
        }}
      >
        {submitting ? "Processing…" : `Pay ${formatMoney(amount)} & file`}
      </button>
      <p style={styles.disabledHint}>
        Cards are processed securely by Stripe — card numbers never touch our servers.
      </p>
    </div>
  );
}

// /checkout/:uuid — pay the filing fee (and optionally set a stake) to file the
// dispute. Paying schedules the AI panel (it runs judgeTiming.label later, server-side).
export default function Checkout() {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const { fetchSharedDispute, payDisputeFee, fileFreeDispute, finalizeDisputePayment } = useAI();
  const judgeTiming = useJudgeTiming();
  const feeConfig = useFeeConfig();
  // Which Stripe keys the site is on right now (admin-switchable) — decides
  // whether the card form can mount at all.
  const { loading: stripeLoading, configured: stripeConfigured } = useStripeConfig();

  const [dispute, setDispute] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [stakingEnabled, setStakingEnabled] = useState(true);
  const [stakeInput, setStakeInput] = useState("");
  const [clientSecret, setClientSecret] = useState(null);
  const [customerSessionClientSecret, setCustomerSessionClientSecret] = useState(null);
  const [amounts, setAmounts] = useState(null); // { fee, stake, total }
  const [starting, setStarting] = useState(false);
  const [initError, setInitError] = useState(null);

  const load = useCallback(async () => {
    const data = await fetchSharedDispute(uuid);
    if (data.error) {
      if (data.status === 401) navigate("/login");
      else setNotFound(true);
    } else setDispute(data);
  }, [uuid, fetchSharedDispute, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const API_BASE = process.env.REACT_APP_API_URL || "";
    fetch(`${API_BASE}/api/ai/staking-config`)
      .then((r) => r.json())
      .then((d) => setStakingEnabled(d.enabled !== false))
      .catch(() => {});
  }, []);

  const startPayment = async () => {
    setStarting(true);
    setInitError(null);
    const stakeCents = Math.round((parseFloat(stakeInput) || 0) * 100);
    const pi = await payDisputeFee(uuid, stakeCents);
    setStarting(false);
    if (pi.error) {
      setInitError(pi.error);
      return;
    }
    setAmounts({ fee: pi.fee, stake: pi.stake, total: pi.amount });
    setClientSecret(pi.clientSecret);
    setCustomerSessionClientSecret(pi.customerSessionClientSecret || null);
  };

  // Zero-fee filing: no PaymentIntent, just fulfill and go to the dispute.
  const fileFree = async () => {
    setStarting(true);
    setInitError(null);
    const result = await fileFreeDispute(uuid);
    setStarting(false);
    if (result?.error) {
      setInitError(result.error);
      return;
    }
    clearDraft();
    navigate(`/dispute/${uuid}?filed=1`);
  };

  const onPaid = async () => {
    // The fee PaymentIntent can still be "processing" the moment confirmPayment
    // resolves; it reaches "succeeded" within a few seconds. Poll finalize a few
    // times before giving up (the server-side webhook also fulfills it as a
    // backstop) so a still-settling payment isn't shown as a failure.
    let result;
    for (let attempt = 0; attempt < 10; attempt++) {
      result = await finalizeDisputePayment(uuid);
      if (!result?.pending) break;
      await new Promise((r) => setTimeout(r, 3000));
    }
    if (result?.error) return { error: result.error };
    if (result?.pending) {
      // Still processing after ~30s — the webhook will finalize it server-side.
      return {
        error:
          "Your payment is still processing. It’ll be confirmed automatically in a moment — refresh this page shortly to see your filed case.",
      };
    }
    if (result?.stakeHoldFailed) {
      // The fee was paid but we couldn't place the stake hold (e.g. the card
      // needed extra authentication). File without a stake and let them know.
      window.alert("Your case was filed, but we couldn't hold your stake on this card, so it was filed without one. You can contact support to add a stake.");
    }
    clearDraft();
    navigate(`/dispute/${uuid}?filed=1`);
    return {};
  };

  if (notFound) {
    return (
      <div style={pageStyles.root}>
        <SiteHeader />
        <div style={styles.loading}>Dispute not found.</div>
      </div>
    );
  }
  if (!dispute) {
    return (
      <div style={pageStyles.root}>
        <SiteHeader />
        <div style={styles.loading}>Loading…</div>
      </div>
    );
  }

  const fee = dispute.filing_fee_amount;
  const alreadyPaid = !!dispute.fee_paid_at;
  // A solo (single-Neutral) case is filed with no fee and no card form.
  const isFree = !fee;

  return (
    <div style={pageStyles.root}>
      <SiteHeader />
      <div style={pageStyles.shell}>
        <div style={styles.stack}>
          {/* Header card (navy) */}
          <div style={styles.headerCard}>
            <div style={styles.headerRow}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h1 style={{ ...pageStyles.pageTitle, color: "#1A5EC4", margin: 0 }}>Submit your dispute</h1>
                <p style={{ ...pageStyles.pageSubtitle, color: COLORS.inkSoft, margin: "8px 0 0 0" }}>
                  One AI Neutral is free. Three Neutrals is {feeConfig.label}.
                </p>
              </div>
              <Handshake size={72} strokeWidth={1.5} style={styles.headerIcon} aria-hidden />
            </div>
          </div>

          {/* Summary */}
          <div style={styles.sectionCard}>
            <h2 style={formStyles.sectionTitle}>Summary</h2>
            <div style={styles.summaryRow}>
              <span style={styles.summaryKey}>Dispute</span>
              <span style={styles.summaryVal}>{dispute.subject || "(no subject)"}</span>
            </div>
            <div style={styles.summaryRow}>
              <span style={styles.summaryKey}>Submission fee</span>
              <span style={styles.summaryVal}>{isFree ? "Free — one AI Neutral" : formatMoney(fee)}</span>
            </div>
            {amounts?.stake > 0 && (
              <div style={styles.summaryRow}>
                <span style={styles.summaryKey}>Your stake (held)</span>
                <span style={styles.summaryVal}>{formatMoney(amounts.stake)}</span>
              </div>
            )}
            <div style={{ ...styles.summaryRow, borderBottom: "none" }}>
              <span style={styles.summaryKey}>Decision</span>
              <span style={styles.summaryVal}>
                Delivered once all parties respond, or after {judgeTiming.label}
              </span>
            </div>
          </div>

          {/* Payment */}
          <div style={styles.sectionCard}>
            <h2 style={formStyles.sectionTitle}>{isFree ? "File" : "Payment"}</h2>

            {!alreadyPaid && !isFree && stripeConfigured && <StripeTestCardHint />}

            {alreadyPaid ? (
              <div style={styles.paidNote}>
                <span>✅ This case is already paid.</span>
                <Link to={`/dispute/${uuid}`} style={styles.linkBtn}>View dispute →</Link>
              </div>
            ) : isFree ? (
              // Solo (single-Neutral) cases carry no fee: file straight through
              // with no card form. Staking needs a paid panel, so it's not
              // offered here.
              <div>
                <p style={styles.hint}>
                  This dispute uses one AI Neutral, so there's nothing to pay. File it now and we'll
                  invite the other party.
                </p>
                {initError && <div style={styles.payError}>{initError}</div>}
                <button
                  type="button"
                  onClick={fileFree}
                  disabled={starting}
                  style={{
                    ...formStyles.primaryButton,
                    ...(starting ? formStyles.primaryButtonDisabled : { background: "#1A5EC4", color: "#fff" }),
                    marginTop: 18,
                  }}
                >
                  {starting ? "Filing…" : "File dispute — free"}
                </button>
              </div>
            ) : stripeLoading ? (
              <div style={styles.hint}>Loading payment form…</div>
            ) : !stripeConfigured ? (
              <div style={styles.setupNote}>
                <strong>Card payment isn't enabled yet.</strong>
                <p style={{ margin: "8px 0 0 0" }}>
                  Set the Stripe secret + publishable keys for the active mode (see AdminConfig →
                  Stripe mode) to enable the secure card form. Your case is saved as a draft until
                  then.
                </p>
              </div>
            ) : !clientSecret ? (
              <div>
                {stakingEnabled && (
                  <div style={formStyles.fieldGroup}>
                    <label style={formStyles.fieldLabel}>Add a stake (optional, USD)</label>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      style={formStyles.textInput}
                      placeholder="0.00"
                      value={stakeInput}
                      onChange={(e) => setStakeInput(e.target.value)}
                    />
                    <p style={styles.hint}>
                      A stake the other party must match within {judgeTiming.label}. We only place a temporary hold
                      on your card for it now — it's charged only if they match. If they match, the
                      winner takes 75% of the pot; if they don't, the hold is released. Minimum $10 if
                      set. Leave empty for no stake.
                    </p>
                  </div>
                )}
                {initError && <div style={styles.payError}>{initError}</div>}
                <button
                  type="button"
                  onClick={startPayment}
                  disabled={starting}
                  style={{
                    ...formStyles.primaryButton,
                    ...(starting ? formStyles.primaryButtonDisabled : { background: "#1A5EC4", color: "#fff" }),
                  }}
                >
                  {starting ? "Preparing…" : "Continue to payment"}
                </button>
              </div>
            ) : (
              <div>
                <div style={styles.totalRow}>
                  <span>Charged now</span>
                  <strong>{formatMoney(amounts?.total ?? fee)}</strong>
                </div>
                {amounts?.stake > 0 && (
                  <p style={styles.hint}>
                    Plus a temporary <strong>{formatMoney(amounts.stake)}</strong> hold for your stake —
                    only captured if the other party matches it.
                  </p>
                )}
                <Elements
                  stripe={getStripePromise()}
                  options={{
                    clientSecret,
                    ...(customerSessionClientSecret ? { customerSessionClientSecret } : {}),
                    appearance: { theme: "stripe" },
                  }}
                >
                  <PayForm uuid={uuid} amount={amounts?.total ?? fee} onPaid={onPaid} />
                </Elements>
                <button
                  type="button"
                  style={styles.backLink}
                  onClick={() => { setClientSecret(null); setAmounts(null); }}
                >
                  ← change amount
                </button>
              </div>
            )}
          </div>

          <button type="button" style={styles.backLink} onClick={() => navigate("/submit-dispute")}>
            ← Back
          </button>
        </div>
      </div>
      <SiteFooter />
    </div>
  );
}

const styles = {
  loading: {
    display: "flex",
    height: "60vh",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 14,
    color: COLORS.inkMuted,
    fontFamily: SANS,
  },
  stack: { width: "100%", maxWidth: 760, display: "flex", flexDirection: "column", gap: 20 },
  // Header card styling mirrors SubmitDispute (white + blue border, blue title
  // and icon) so the file → pay flow reads as one page.
  headerCard: {
    width: "100%",
    background: "#fff",
    color: COLORS.ink,
    borderRadius: "20px 20px 20px 4px",
    padding: "32px 40px",
    boxShadow: "0 6px 28px rgba(15, 30, 45, 0.10)",
    border: "2px solid rgba(26,94,196,0.2)",
  },
  headerRow: { display: "flex", alignItems: "center", gap: 24 },
  headerIcon: { color: "#1A5EC4", flexShrink: 0, opacity: 0.9 },
  sectionCard: {
    width: "100%",
    background: COLORS.card,
    borderRadius: "20px 20px 20px 4px",
    padding: "32px 40px",
    boxShadow: "0 6px 28px rgba(15, 30, 45, 0.06)",
    border: `1px solid ${COLORS.border}`,
  },
  summaryRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 16,
    padding: "12px 0",
    borderBottom: `1px solid ${COLORS.border}`,
  },
  summaryKey: { fontSize: 12, color: COLORS.inkMuted, textTransform: "uppercase", letterSpacing: 1 },
  summaryVal: { fontSize: 14, color: COLORS.ink, fontWeight: 500, textAlign: "right" },
  totalRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontSize: 15,
    color: COLORS.ink,
    padding: "0 0 16px 0",
    marginBottom: 4,
    borderBottom: `1px solid ${COLORS.border}`,
  },
  hint: { color: COLORS.inkSoft, fontSize: 12, margin: "8px 0 0 0", lineHeight: 1.5 },
  setupNote: {
    background: COLORS.highlight,
    border: `1px solid ${COLORS.borderStrong}`,
    borderRadius: 10,
    padding: "14px 16px",
    fontSize: 13,
    color: COLORS.ink,
    lineHeight: 1.5,
  },
  payError: {
    background: "#fef2f2",
    color: "#991b1b",
    border: "1px solid #fecaca",
    borderRadius: 8,
    padding: "10px 12px",
    fontSize: 13,
    margin: "12px 0",
  },
  paidNote: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    fontSize: 14,
    color: COLORS.ink,
  },
  linkBtn: { color: COLORS.ink, fontWeight: 600, textDecoration: "none" },
  disabledHint: { color: COLORS.inkMuted, fontSize: 12, margin: "10px 0 0 0", textAlign: "center" },
  backLink: {
    background: "transparent",
    border: "none",
    color: COLORS.inkSoft,
    fontSize: 13,
    cursor: "pointer",
    fontFamily: SANS,
    alignSelf: "flex-start",
    marginTop: 12,
    padding: 0,
  },
};
